/**
 * Display mirror of `statutoryRightsDeadline` in backend/src/lib/dataProtectionScope.ts: the
 * deadline for a data subject request. The server computes and enforces it; this only previews it.
 *
 * Each law in effect keeps its own base period and its own extension, in its own unit. The first
 * deadline is the earliest base period; an extended deadline is the earliest outer limit (a law's base
 * plus its own extension, or the base alone where it allows none) — never one law's base with another
 * law's extension. A law that counts in months ("one month", "two further months") is counted in
 * calendar months: received 1 February is due 1 March, received 31 January is due on the last day of
 * February — never 30 days, which overshoots the legal deadline in February. Time the clock was
 * stopped (UK GDPR Art. 12A) moves only the laws that allow it.
 */

export interface RightsClock {
  value: number;
  regulation: string;
  label: string;
  /** Calendar months where the law counts in months; null / absent for day or week clocks. */
  months?: number | null;
}

export interface RightsLawClock {
  regulation: string;
  rights: RightsClock;
  extension: RightsClock | null;
  pause: boolean;
}

export interface RightsClocks {
  rights: RightsClock;
  rightsExtension: RightsClock | null;
  rightsLaws?: readonly RightsLawClock[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The same date `months` later (UTC), or the last day of that month when it has no such date. */
export function addCalendarMonths(from: Date, months: number): Date {
  const y = from.getUTCFullYear();
  const m = from.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(
    y, m, Math.min(from.getUTCDate(), lastDay),
    from.getUTCHours(), from.getUTCMinutes(), from.getUTCSeconds(), from.getUTCMilliseconds(),
  ));
}

export function addClock(from: Date, clock: RightsClock): Date {
  return clock.months ? addCalendarMonths(from, clock.months) : new Date(from.getTime() + clock.value * DAY_MS);
}

export interface RightsDeadlineOptions {
  extended?: boolean;
  pausedMs?: number;
}

/** The latest date a request received at `receivedAt` may be due (`extended`: once the one extension is claimed). */
export function statutoryRightsDeadline(
  receivedAt: Date,
  clocks: RightsClocks,
  options: boolean | RightsDeadlineOptions = false,
): Date {
  const opts: RightsDeadlineOptions = typeof options === "boolean" ? { extended: options } : options;
  const laws: readonly RightsLawClock[] = clocks.rightsLaws?.length
    ? clocks.rightsLaws
    : [{ regulation: clocks.rights.regulation, rights: clocks.rights, extension: clocks.rightsExtension, pause: false }];
  const paused = Math.max(0, opts.pausedMs ?? 0);
  let earliest: Date | null = null;
  for (const law of laws) {
    let due = addClock(receivedAt, law.rights);
    if (opts.extended && law.extension) due = addClock(due, law.extension);
    if (law.pause && paused > 0) due = new Date(due.getTime() + paused);
    if (earliest === null || due.getTime() < earliest.getTime()) earliest = due;
  }
  return earliest as Date;
}

/** Total time stopped, counting an open pause to `asOf` (mirror of `pausedMs`). */
export function pausedMs(pauses: ReadonlyArray<{ from: string | Date; to: string | Date | null }>, asOf: Date = new Date()): number {
  const spans = pauses
    .map((p) => [new Date(p.from).getTime(), (p.to ? new Date(p.to) : asOf).getTime()] as const)
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  let total = 0;
  let end = -Infinity;
  for (const [a, b] of spans) {
    if (b <= end) continue;
    total += b - Math.max(a, end);
    end = b;
  }
  return total;
}

/**
 * How much of each stop counts for a request (mirror of backend lib/rightsRequests.ts `countedPauses`): identity
 * information and a fee from receipt until they arrive (UK GDPR Art. 12A(2)); clarification only for an access
 * request (12A(5)).
 */
export function countedPauses<P extends { reason: string; from: string | Date; to: string | Date | null }>(
  pauses: readonly P[], type: string, receivedAt: string | Date,
): P[] {
  return pauses
    .filter((p) => p.reason !== "clarification" || type === "access")
    .map((p) => (p.reason === "identity" || p.reason === "fee") ? { ...p, from: receivedAt } : p);
}

/** "one month" / "30 days" — how long a clock is, in the law's own unit. */
export function clockLength(clock: RightsClock): string {
  return clock.months ? clock.label : `${clock.value} days`;
}
